/**
 * Filter toolbar + its controls.
 *
 * Rhythm note (locked): the toolbar BELONGS to its table — they sit together in
 * one `gap-150` group, while the KPI row above is a separate `gap-200` section.
 */
import {
  For,
  Show,
  children,
  createMemo,
  createSignal,
  type JSX,
  type ParentProps,
} from "solid-js";
import Search from "lucide-solid/icons/search";
import X from "lucide-solid/icons/x";
import Check from "lucide-solid/icons/check";
import ChevronDown from "lucide-solid/icons/chevron-down";
import { Popover } from "./popover";
import { cn } from "./utils/cn";
import { focusRing, focusRingInset } from "./utils/focus-ring";

export function Toolbar(props: ParentProps<{ class?: string }>): JSX.Element {
  return (
    <div class={cn("flex min-w-0 flex-wrap items-center gap-100", props.class)}>
      {props.children}
    </div>
  );
}

export function SearchField(props: {
  value: string;
  onInput: (value: string) => void;
  placeholder?: string;
  class?: string;
}): JSX.Element {
  return (
    <div class={cn("relative min-w-0", props.class)}>
      <Search
        size={16}
        class="pointer-events-none absolute left-100 top-1/2 -translate-y-1/2 text-[color:var(--c9-icon-subtle)]"
      />
      <input
        type="search"
        value={props.value}
        onInput={(e) => props.onInput(e.currentTarget.value)}
        placeholder={props.placeholder ?? "Search"}
        class={cn(
          "h-8 w-full rounded-small border border-[color:var(--c9-border-input)]",
          "bg-[var(--c9-bg-input)] pl-[30px] pr-100 text-body text-[color:var(--c9-text)]",
          "placeholder:text-[color:var(--c9-text-subtlest)] hover:border-[color:var(--c9-border-bold)]",
          focusRing,
        )}
      />
      <Show when={props.value}>
        <button
          type="button"
          aria-label="Clear search"
          onClick={() => props.onInput("")}
          class={cn(
            "absolute right-050 top-1/2 inline-flex size-5 -translate-y-1/2 items-center justify-center",
            "rounded-[3px] text-[color:var(--c9-icon-subtle)] hover:bg-[var(--c9-bg-neutral-subtle-hovered)]",
            focusRing,
          )}
        >
          <X size={14} />
        </button>
      </Show>
    </div>
  );
}

export interface SelectOption<T extends string = string> {
  value: T;
  label: string;
}

/**
 * Single-select dropdown — a custom, on-system control built on the kit's
 * `Popover`, NOT a native `<select>` (the OS picker paints a hard system menu
 * that ignores our theme, and reads badly in dark mode). The trigger is a soft
 * on-surface button; the menu is a portalled overlay list with keyboard nav
 * (↑ ↓ Enter Escape), click-outside close, and a checked current row.
 *
 * The public signature is unchanged from the native version — `value`,
 * `onChange`, `options`, `label`, `class`, `disabled` — plus optional
 * `placeholder`, `width`, and `searchable`.
 */
export function Select<T extends string>(props: {
  value: T;
  onChange: (value: T) => void;
  options: SelectOption<T>[];
  label?: string;
  class?: string;
  disabled?: boolean;
  placeholder?: string;
  /** Menu-panel width in px (default 220). */
  width?: number;
  /** Show an in-menu search field that filters options by label. */
  searchable?: boolean;
}): JSX.Element {
  const [query, setQuery] = createSignal("");
  const [active, setActive] = createSignal(-1);
  let rows: (HTMLButtonElement | undefined)[] = [];

  const current = () => props.options.find((o) => o.value === props.value);
  const filtered = createMemo(() => {
    if (!props.searchable) return props.options;
    const q = query().trim().toLowerCase();
    return q ? props.options.filter((o) => o.label.toLowerCase().includes(q)) : props.options;
  });

  const syncActive = () => {
    const list = filtered();
    const i = list.findIndex((o) => o.value === props.value);
    setActive(i >= 0 ? i : list.length ? 0 : -1);
  };

  const scrollTo = (i: number) => {
    queueMicrotask(() => rows[i]?.scrollIntoView({ block: "nearest" }));
  };

  const move = (delta: number) => {
    const list = filtered();
    if (!list.length) return;
    const next = (active() + delta + list.length) % list.length;
    setActive(next);
    scrollTo(next);
  };

  return (
    <Popover
      width={props.width ?? 220}
      class="py-100"
      trigger={({ toggle, open, setRef }) => {
        const openMenu = () => {
          setQuery("");
          syncActive();
          toggle();
        };
        return (
          <button
            ref={setRef}
            type="button"
            disabled={props.disabled}
            aria-haspopup="listbox"
            aria-expanded={open()}
            aria-label={props.label}
            onClick={() => !props.disabled && openMenu()}
            onKeyDown={(e) => {
              if (props.disabled) return;
              if (!open()) {
                if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openMenu();
                }
                return;
              }
              if (e.key === "ArrowDown") {
                e.preventDefault();
                move(1);
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                move(-1);
              } else if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                const opt = filtered()[active()];
                if (opt) {
                  props.onChange(opt.value);
                  toggle();
                }
              } else if (e.key === "Tab") {
                toggle();
              }
            }}
            class={cn(
              "inline-flex h-8 min-w-0 items-center justify-between gap-100 rounded-small",
              "border border-[color:var(--c9-border)] bg-[var(--c9-bg-input)] px-100",
              "text-body font-[570] text-[color:var(--c9-text)]",
              "hover:bg-[var(--c9-bg-input-hovered)]",
              "disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-[var(--c9-bg-input)]",
              focusRing,
              props.class,
            )}
          >
            <span
              class={cn(
                "min-w-0 truncate",
                !current() && "font-normal text-[color:var(--c9-text-subtlest)]",
              )}
            >
              {current()?.label ?? props.placeholder ?? "Select"}
            </span>
            <ChevronDown size={14} class="shrink-0 text-[color:var(--c9-icon-subtle)]" />
          </button>
        );
      }}
    >
      {({ close }) => {
        rows = [];
        return (
          <>
            <Show when={props.searchable}>
              <div class="shrink-0 px-150 pb-100 pt-050">
                <div class="flex h-8 items-center gap-100 rounded-[3px] border border-[color:var(--c9-border-input)] bg-[var(--c9-bg-input)] px-100 focus-within:border-[color:var(--c9-border-focused)] focus-within:shadow-[inset_0_0_0_1px_var(--c9-border-focused)]">
                  <Search size={16} class="shrink-0 text-[color:var(--c9-text-subtlest)]" />
                  <input
                    type="text"
                    value={query()}
                    placeholder="Search"
                    aria-label="Search options"
                    onInput={(e) => {
                      setQuery(e.currentTarget.value);
                      syncActive();
                    }}
                    class="h-5 w-full min-w-0 appearance-none border-0 bg-transparent p-0 text-body leading-5 text-[color:var(--c9-text)] outline-none placeholder:text-[color:var(--c9-text-subtlest)]"
                  />
                </div>
              </div>
            </Show>
            <div role="listbox" class="min-h-0 flex-1 overflow-y-auto">
              <For
                each={filtered()}
                fallback={
                  <p class="px-150 py-100 text-body-s text-[color:var(--c9-text-subtlest)]">
                    No matches
                  </p>
                }
              >
                {(opt, i) => {
                  const selected = () => opt.value === props.value;
                  return (
                    <button
                      ref={(el) => (rows[i()] = el)}
                      type="button"
                      role="option"
                      aria-selected={selected()}
                      onMouseEnter={() => setActive(i())}
                      onClick={() => {
                        props.onChange(opt.value);
                        close();
                      }}
                      class={cn(
                        "flex w-full items-center gap-100 px-150 py-075 text-left text-body text-[color:var(--c9-text)] transition-colors",
                        active() === i() && "bg-[var(--c9-bg-neutral-subtle-hovered)]",
                        selected() &&
                          "bg-[var(--c9-bg-neutral-subtle-hovered)] shadow-[inset_2px_0_0_0_var(--c9-border-selected)]",
                        focusRingInset,
                      )}
                    >
                      <span class="min-w-0 flex-1 truncate">{opt.label}</span>
                      <Show when={selected()}>
                        <Check size={16} class="shrink-0 text-[color:var(--c9-text-selected)]" />
                      </Show>
                    </button>
                  );
                }}
              </For>
            </div>
          </>
        );
      }}
    </Popover>
  );
}

/** A labelled group in a toolbar, e.g. "Status: [select]". */
export function ToolbarField(
  props: ParentProps<{ label: string; class?: string }>,
): JSX.Element {
  const content = children(() => props.children);
  return (
    <label class={cn("flex min-w-0 items-center gap-075", props.class)}>
      <span class="shrink-0 text-body-s font-[630] text-[color:var(--c9-text-subtlest)]">
        {props.label}
      </span>
      {content()}
    </label>
  );
}
